import { useCallback, useState } from 'react';

import styled from '@emotion/styled';
import { generateUtilityClasses } from '@mui/material';
import { Grid } from '@kc-console/common-ui';
import {
  LayoutType,
  PageLayoutProvider,
  SplitPanelId,
  SplitPanelInnerId,
} from '@kc-console/context';

import { DEFAULT_LAYOUT_STYLE } from '../../constants/layout';
import { useDraggablePanelLayoutV2 } from '../../hooks/useDraggablePanelLayoutV2';
import { usePanelViewport } from '../../hooks/usePanelViewport';
import { BannerRenderer } from '../BannerRenderer';
import { PanelPortal } from './PanelLayout';
import {
  LayoutBannerContainerWrapper,
  LayoutBodyContainerWrapper,
  LayoutContentSectionWrapper,
  LayoutHeaderContainerWrapper,
  LayoutSplitSectionWrapper,
  LayoutWrapper,
} from './Wrapper';

import type {
  DefaultComponentProps,
  ListSectionProps,
  ListSplitSectionWrapperProps,
} from './types';

export const listSectionClasses = generateUtilityClasses('ListSection', [
  'content',
  'banner',
  'header',
  'body',
]);

/**
 * ListLayout
 *
 * - usePanelViewport: PanelNavigation 직접 구독
 * - useDraggablePanelLayoutV2: usePanelPreferenceStore 기반 리사이즈
 */
export default function ListLayout(props: DefaultComponentProps) {
  const { className, children } = props;
  return (
    <PageLayoutProvider layoutType={LayoutType.list}>
      <LayoutWrapper className={className}>
        {children}
        <ListPanelViewport />
      </LayoutWrapper>
    </PageLayoutProvider>
  );
}

export const ListSection = (props: ListSectionProps) => {
  const { headerView, children } = props;

  return (
    <>
      <ListContentSection className={listSectionClasses.content}>
        <ListBannerContainer className={listSectionClasses.banner}>
          <BannerRenderer />
        </ListBannerContainer>
        <ListHeaderContainer className={listSectionClasses.header}>
          {headerView}
        </ListHeaderContainer>
        <ListBodyContainer className={listSectionClasses.body}>{children}</ListBodyContainer>
      </ListContentSection>
      <ListSplitSection />
    </>
  );
};

/**
 * V2 패널 뷰포트 - PanelNavigation 직접 구독
 */
const ListPanelViewport = () => {
  const { visible, targetPanel } = usePanelViewport();
  const [localVisible, setLocalVisible] = useState(visible);

  const transitionendCallback = useCallback(() => {
    setLocalVisible(visible);
  }, [visible]);

  return (
    <PanelPortal
      targetPanel={targetPanel}
      localVisible={localVisible || visible}
      transitionendCallback={transitionendCallback}
    />
  );
};

const ListContentSection = styled(LayoutContentSectionWrapper)`
  min-height: 0;
  overflow: auto;
`;

const ListBannerContainer = styled(LayoutBannerContainerWrapper)`
  min-width: ${DEFAULT_LAYOUT_STYLE.MIN_WIDTH}px;
`;
const ListHeaderContainer = styled(LayoutHeaderContainerWrapper)`
  min-width: ${DEFAULT_LAYOUT_STYLE.MIN_WIDTH}px;
`;

const ListBodyContainer = styled(LayoutBodyContainerWrapper)`
  min-height: 0;
  min-width: ${DEFAULT_LAYOUT_STYLE.MIN_WIDTH}px;
`;

/**
 * V2 Split Section - useDraggablePanelLayoutV2 기반
 */
const ListSplitSection = () => {
  const { visible } = usePanelViewport();
  const { dragBarRef, targetPanelSize, handleMouseDown } = useDraggablePanelLayoutV2({
    layoutType: 'list',
    visible,
  });

  return (
    <ListSplitSectionWrapper
      ref={dragBarRef}
      id={SplitPanelId}
      container
      visible={visible}
      height={targetPanelSize}
    >
      <ListSplitSectionInnerDividerWrapper
        container
        justifyContent="center"
        alignItems="end"
        onMouseDown={handleMouseDown}
      >
        <ListSplitSectionBar visible={visible} />
      </ListSplitSectionInnerDividerWrapper>
      <ListSplitSectionInnerWrapper id={SplitPanelInnerId} container />
    </ListSplitSectionWrapper>
  );
};

const ListSplitSectionWrapper = styled(LayoutSplitSectionWrapper, {
  shouldForwardProp: prop => prop !== 'visible',
})<ListSplitSectionWrapperProps>`
  margin-top: ${({ visible }) => (visible ? '8px' : 0)};
  height: ${({ visible, height }) => (visible ? `${height}px` : 0)};
  flex-direction: column;
  flex-wrap: nowrap;
  transition:
    height 0.3s,
    margin-top 0.3s;
`;

const ListSplitSectionInnerDividerWrapper = styled(Grid)`
  height: 11px;
  width: 100%;
  cursor: ns-resize;
`;

const ListSplitSectionBar = styled(Grid, {
  shouldForwardProp: prop => prop !== 'visible',
})<Pick<ListSplitSectionWrapperProps, 'visible'>>`
  width: 24px;
  height: ${({ visible }) => (visible ? '3px' : 0)};
  background-color: ${({ theme }) => theme.color['icon/weakest/default']};
`;

const ListSplitSectionInnerWrapper = styled(Grid)`
  height: 100%;
`;
